import { I18nAndL10n } from '@modules/i18n_and_l10n/mod';
import { Logger } from '@modules/logger/logger';
import { Singleton } from '@visiba/instantiation';
import { applicationLocalStorage } from '../application_local_storage';
import { LicenceHolderService } from './licence_holder.service';

// TODO: DEV-10753 Find a new home for these interfaces.
interface MigrationVerifier {
	readonly migrationVersion: number;
}
interface DataIteration_v0 {
	readonly selectedLanguage: I18nAndL10n.AvailableVisibaLocales;
}

export type Settings = MigrationVerifier & DataIteration_v0;

export class UserSettingsNotSetError extends Error {
	constructor(public message: string) {
		super(message);
		this.name = 'UserSettingsNotSetError';
		Object.setPrototypeOf(this, UserSettingsNotSetError.prototype);
	}
}

@Singleton()
export class UserSettingsService {
	private static readonly storageKey = 'USER_SETTINGS';
	private readonly storage = applicationLocalStorage;
	private readonly activeMigrationVersion = 0;
	private storedSettings: Settings | null = null;

	public getSettings(): Omit<Settings, 'migrationVersion'>;
	public getSettings(failGracefully: boolean): Omit<Settings, 'migrationVersion'> | null;
	public getSettings(failGracefully?: boolean): Omit<Settings, 'migrationVersion'> | null {
		let settings: Settings;
		if (this.storedSettings == null) {
			if (failGracefully) return null;

			settings = this.loadSettings();
		} else {
			settings = this.storedSettings;
		}

		return settings;
	}

	constructor(private readonly licenceHolderService: LicenceHolderService) {
		// Empty
	}

	public loadSettings(): Settings {
		const data = this.storage.get(UserSettingsService.storageKey);
		if (data == null) {
			this.storedSettings = this.createNewSettings();
			this.save(this.storedSettings);

			return this.storedSettings;
		}

		this.storedSettings = data;
		this.updateSettings(data);

		return data;
	}

	public updateSettings(settings: Partial<Omit<Settings, 'migrationVersion'>>): void {
		// Casting this as the actual data structure it's store as
		const currentSettings = this.getSettings() as Settings;

		// TODO: DEV-10753 Add migration logic
		// if (this.activeMigrationVersion !== currentSettings.migrationVersion) {
		// 	const migration = new MigrationStrategyPipe(currentSettings);
		// 	this.storedSettings = migration.migrate(settings);

		// 	return;
		// }

		const builder: Settings = {
			...currentSettings,
			...settings,
		};

		this.save(builder);
		this.storedSettings = builder;
	}

	public setLanguage(): void {
		const { selectedLanguage } = this.getSettings();
		Logger.assert(Logger.Level.info, `Sets language to ${selectedLanguage}`);

		I18nAndL10n.updateLangCode(selectedLanguage);
	}

	private save(data: Settings): void {
		this.storage.set(UserSettingsService.storageKey, data);
	}

	private createNewSettings(): Settings {
		return {
			migrationVersion: this.activeMigrationVersion,
			selectedLanguage: this.retrieveSelectedLanguage(),
		};
	}

	private retrieveSelectedLanguage(): I18nAndL10n.AvailableVisibaLocales {
		// TODO: DEV-10753 Remove this in favour of migration logic
		const OLD_PATIENT_WEB_LANG = localStorage.getItem('languageCode') as string | null;

		const languagesToTry = new Set([OLD_PATIENT_WEB_LANG, navigator.language, ...navigator.languages].filter(Boolean) as string[]);

		function tryToSelectLanguage(str: string): I18nAndL10n.AvailableVisibaLocales | null {
			try {
				return I18nAndL10n.convertExternalLocaleToAvailableLocale(str);
			} catch (_: unknown) {
				return null;
			}
		}

		let selectedLanguage = this.licenceHolderService.rootLanguageCode;
		for (const language of languagesToTry) {
			const selectedLanguageCandidate = tryToSelectLanguage(language);
			if (selectedLanguageCandidate != null) {
				selectedLanguage = selectedLanguageCandidate;
				break;
			}
		}

		return selectedLanguage;
	}
}
